import { Component, Injectable, Input, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import {
  EventVideoUnlockOptionSecurityResource,
  EventVideoUnlockOptionSecurityResourceAction,
} from '@greco/event-videos';
import { Community } from '@greco/identity-communities';
import { BuildSearchFilter, BuildTextFilter } from '@greco/ngx-filters';
import { CommunitySecurityService } from '@greco/ngx-identity-community-staff-util';
import { PropertyListener } from '@greco/property-listener-util';
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request';
import type { IPaginationMeta, IPaginationOptions } from 'nestjs-typeorm-paginate';
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { debounceTime, first, map, switchMap, tap } from 'rxjs/operators';
import { CreateCommunityVideoUnlockOptionDialog } from '../../dialogs';
import { CommunityVideoUnlockOptionsService } from '../../services';

@Injectable({ providedIn: 'root' })
export class CommunityOptionsSearchTitleFilter extends BuildTextFilter('CommunityOptionsSearchTitleFilter', {
  label: 'Unlock Option',
  shortLabel: 'Unlock Option',
  description: '',
  allowedOperators: [CondOperator.CONTAINS_LOW, CondOperator.EQUALS_LOW],
  properties: ['title'],
}) {}

@Injectable({ providedIn: 'root' })
export class CommunityOptionsSearchTagFilter extends BuildTextFilter('CommunityOptionsSearchTagFilter', {
  label: 'Video Tags',
  shortLabel: 'Video Tags',
  description: '',
  allowedOperators: [CondOperator.CONTAINS_LOW, CondOperator.EQUALS_LOW],
  properties: ['videoTags.label'],
}) {}

@Injectable({ providedIn: 'root' })
export class CommunityOptionsSearchFilter extends BuildSearchFilter('CommunityOptionsSearchFilter', {
  properties: ['title', 'videoTags.label'],
  propertyLabels: ['Unlock Option', 'Video Tags'],
}) {}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'greco-admin-community-video-unlock-options-page',
  templateUrl: './admin-unlock-options.page.html',
  styleUrls: ['./admin-unlock-options.page.scss'],
})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export class AdminCommunityVideoUnlockOptionsPage implements OnDestroy {
  constructor(
    private unlockOptSvc: CommunityVideoUnlockOptionsService,
    private matDialog: MatDialog,
    private comSecSvc: CommunitySecurityService
  ) {}

  @Input() community!: Community;
  @PropertyListener('community') private community$ = new ReplaySubject<Community | null>(1);

  _loading = true;
  _paginationMeta?: IPaginationMeta;
  _filterOptions = [CommunityOptionsSearchTitleFilter, CommunityOptionsSearchTagFilter, CommunityOptionsSearchFilter];
  _filters$ = new BehaviorSubject<RequestQueryBuilder>(new RequestQueryBuilder());
  _refresh$ = new BehaviorSubject(null);
  _pagination$ = new BehaviorSubject<IPaginationOptions>({ page: 1, limit: 10 });

  canManageUnlockOptions$ = this.community$.pipe(
    switchMap(async community =>
      community
        ? await this.comSecSvc.hasAccess(
            community.id,
            EventVideoUnlockOptionSecurityResource.key,
            EventVideoUnlockOptionSecurityResourceAction.MANAGE
          )
        : false
    )
  );

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  _unlockOptions$ = combineLatest([this._filters$, this.community$, this._pagination$, this._refresh$]).pipe(
    tap(() => (this._loading = true)),
    debounceTime(500),
    switchMap(async ([query, community, page]) => {
      return query && community ? await this.unlockOptSvc.paginate(query, community.id, page) : null;
    }),
    tap(data => (this._paginationMeta = data?.meta)),
    map(data => data?.items || []),
    tap(() => (this._loading = false))
  );

  async createNewUnlockOption() {
    if (!this.community) return;

    this.matDialog
      .open(CreateCommunityVideoUnlockOptionDialog, { data: { communityId: this.community.id } })
      .afterClosed()
      .pipe(first())
      .subscribe(() => this.refresh());
  }

  refresh() {
    this._pagination$.next(this._pagination$.value);
  }

  ngOnDestroy() {
    this.community$.complete();
    this._pagination$.complete();
  }
  onFilterApplied() {
    if (this.paginator !== undefined) this.paginator.firstPage();
  }
}
